import { isBefore, startOfDay } from "date-fns"
import { z } from "zod"
import { textAreaDefaultMaxLength } from "@/models/common"
import { useChMedicationSearchItemQueryProductQuery$data } from "@/modules/patients/detail/medication/services/__generated__/useChMedicationSearchItemQueryProductQuery.graphql"
import { useChMedicationSearchQuery$data } from "@/modules/patients/detail/medication/services/__generated__/useChMedicationSearchQuery.graphql"
import { MedicationUnitValues } from "@/types/__generated__/union-types"

type MedicationDetail = Partial<
  useChMedicationSearchQuery$data["ch_medicationSearch"]["results"][number] &
    useChMedicationSearchItemQueryProductQuery$data["ch_medicationProductSearchItem"]
>

export type PreparationDetailArticle = {
  readonly description: string
  readonly globalTradeItemNumber: string | null
  readonly pharmacode: number | null
  readonly publicPrice: number | null
}
export type Dosage = "morningDosage" | "noonDosage" | "eveningDosage" | "nightDosage"
export type MedicationDate = "startDate" | "endDate"
export type ChMedicationField =
  | "medication"
  | "substance"
  | "medicationTemplate"
  | "preparation"
  | "freeTextMedicationName"

export const usageInstructionsMaxLength = 4000

export const MedicationSchema = z
  .object({
    morningDosage: z.string(),
    noonDosage: z.string(),
    eveningDosage: z.string(),
    nightDosage: z.string(),
    unit: z.enum(MedicationUnitValues),
    usageInstructions: z
      .string()
      .max(usageInstructionsMaxLength, { message: "common.fieldErrorList.characterLimit" })
      .nullable(),
    internalNotes: z
      .string()
      .max(textAreaDefaultMaxLength, { message: "common.fieldErrorList.characterLimit" })
      .nullable(),
    medication: z.custom<MedicationDetail>().nullish(),
    preparation: z.custom<PreparationDetailArticle>().nullish(),
    startDate: z.date().optional(),
    endDate: z.date().optional(),
    freeTextMedicationName: z
      .string()
      .trim()
      .max(250, { message: "common.fieldErrorList.characterLimit" })
      .nullable()
      .optional(),
    globalTradeItemNumber: z.string().optional(),
    numberOfPackages: z.number().min(1).max(9).default(1),
    refuseSubstitutes: z.boolean().optional().default(false),
    asNeeded: z.boolean().optional(),
    isPermanentPrescription: z.boolean().optional(),
  })
  .superRefine(({ medication, startDate, endDate, freeTextMedicationName, asNeeded, ...rest }, ctx) => {
    if (!medication && !freeTextMedicationName) {
      ctx.addIssue({
        code: "custom",
        path: ["medication"],
        message: "common.fieldErrorList.requiredField",
      })
      ctx.addIssue({
        code: "custom",
        path: ["freeTextMedicationName"],
        message: "common.fieldErrorList.requiredField",
      })
    }
    if (startDate && endDate && isBefore(startOfDay(endDate), startOfDay(startDate))) {
      ctx.addIssue({
        code: "custom",
        path: ["startDate"],
        message: "common.fieldErrorList.invalidDateRange",
      })
      ctx.addIssue({
        code: "custom",
        path: ["endDate"],
        message: "common.fieldErrorList.invalidDateRange",
      })
    }
    if (!asNeeded) {
      const dosages: Array<Dosage> = ["morningDosage", "noonDosage", "eveningDosage", "nightDosage"]
      dosages.forEach((dosage) => {
        if (!rest[dosage]) {
          ctx.addIssue({
            code: "custom",
            path: [dosage],
            message: "common.fieldErrorList.requiredField",
          })
        }
      })
    }
  })

export type MedicationSchemaType = z.infer<typeof MedicationSchema>
export type MedicationSchemaInput = z.input<typeof MedicationSchema>
